// Prevents Backspace/Delete from structurally removing top-level newsletter
// blocks or images. Inline character deletion inside text still works.
//
// Returning `true` from a Tiptap keyboard-shortcut handler reports the key as
// handled and suppresses the default behavior. Returning `false` lets PM run
// its default deletion.

import { Extension } from "@tiptap/core"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

import { nodeInGroup } from "./attributes"

export const StructuralDeleteGuard = Extension.create({
  name: "structuralDeleteGuard",

  addKeyboardShortcuts() {
    return {
      Backspace: () => shouldBlock(this.editor, "Backspace"),
      "Mod-Backspace": () => shouldBlock(this.editor, "Backspace"),
      Delete: () => shouldBlock(this.editor, "Delete"),
      "Mod-Delete": () => shouldBlock(this.editor, "Delete"),
    }
  },
})

function shouldBlock(editor, key) {
  const sel = editor.state.selection

  if (sel instanceof NodeSelection) {
    const type = sel.node.type
    // Block structural deletion of any top-level newsletterBlock — and of an
    // image media atom (whether it sits in a standalone figure or a row).
    if (nodeInGroup(type, "newsletterBlock")) return true
    if (type.name === "newsletterImageMedia") return true
    return false
  }

  if (!(sel instanceof TextSelection)) return false

  // Range selection — block if it spans more than one top-level block. Inline
  // ranges inside a single block delete normally.
  if (sel.from !== sel.to) {
    const fromBlock = topLevelStart(sel.$from)
    const toBlock = topLevelStart(sel.$to)
    if (fromBlock === null || toBlock === null || fromBlock !== toBlock) return true
    return false
  }

  // Caret only — block at top-level block boundaries where the default would
  // merge or remove a block.
  const $pos = sel.$from
  if ($pos.depth === 0) return true

  if ($pos.depth === 1) {
    if (key === "Backspace" && $pos.parentOffset === 0) return true
    if (key === "Delete" && $pos.parentOffset === $pos.parent.content.size) return true
  }
  return false
}

function topLevelStart($pos) {
  if ($pos.depth < 1) return null
  return $pos.before(1)
}
